import api from '~api/api'
import {
  type CaseFilterRequest,
  type CaseId,
  type FormStepId,
  type InputStepId,
  type StepId,
  type UserId,
  type WorkflowId,
} from '~types'

export interface CasePdfOptions {
  userId: UserId
  caseId: CaseId
  stepIds: StepId[]
  includeEmails: boolean
  includeLogs: boolean
  includeTasks: boolean
  includeDescription: boolean
  includeCaseDetails: boolean
  selectedInputsByStep?: Record<FormStepId, InputStepId[]>
  writeTiff?: boolean
  /** Case files to ship alongside the PDF; any selection turns the export into a ZIP. */
  selectedFileKeys?: string[]
}

async function requestCaseExport(options: CasePdfOptions, expectedType: string): Promise<Blob> {
  const {
    userId,
    caseId,
    stepIds,
    includeEmails,
    includeLogs,
    includeTasks,
    includeDescription,
    includeCaseDetails,
    selectedInputsByStep,
    writeTiff,
    selectedFileKeys,
  } = options

  const response = await api.post(
    '/cases/print-case/',
    {
      user_id: userId,
      case_id: caseId,
      step_ids: stepIds,
      write_tiff: writeTiff ?? false,
      include_emails: includeEmails,
      include_logs: includeLogs,
      include_tasks: includeTasks,
      include_description: includeDescription,
      include_case_details: includeCaseDetails,
      selected_inputs_by_step: selectedInputsByStep || undefined,
      selected_file_keys: selectedFileKeys?.length ? selectedFileKeys : undefined,
    },
    { responseType: 'blob' },
  )

  const contentType = response.headers['content-type']
  if (contentType !== expectedType) {
    throw new Error(`Expected ${expectedType} but received ${contentType}`)
  }

  return new Blob([response.data], { type: expectedType })
}

export async function generateCasePdfBlob(options: CasePdfOptions): Promise<Blob> {
  return requestCaseExport(
    { ...options, selectedFileKeys: undefined },
    options.writeTiff ? 'image/tiff' : 'application/pdf',
  )
}

/** The case PDF plus the selected case files, each in its original format. */
export async function generateCaseExportZipBlob(
  options: CasePdfOptions & { selectedFileKeys: string[] },
): Promise<Blob> {
  return requestCaseExport({ ...options, writeTiff: false }, 'application/zip')
}

export interface AllCasesDownloadOptions {
  userId: UserId
  workflowId: WorkflowId
  stepIds: StepId[]
  organizationIdentityIdList: string[]
  writeTiff?: boolean
  caseFilter?: CaseFilterRequest | null
}

/**
 * Kicks off an asynchronous download of the selected steps across every case in
 * a workflow, across all of its versions. The backend zips the generated documents and emails a download
 * link to the requesting user. Resolves with the email recipient address.
 */
export async function downloadAllCasesForWorkflow(
  options: AllCasesDownloadOptions,
): Promise<{ email_recipient: string }> {
  const { userId, workflowId, stepIds, organizationIdentityIdList, writeTiff, caseFilter } = options

  const response = await api.post('/cases/print-case/', {
    user_id: userId,
    workflow_id: workflowId,
    step_ids: stepIds,
    download_all_cases: true,
    write_tiff: writeTiff ?? false,
    organization_identity_id_list: organizationIdentityIdList,
    case_filter: caseFilter ?? undefined,
  })

  return response.data
}
